import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';

import { FontResizerComponent } from '@frontend/sports/common/core/feature/font-resizer';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { SportsThemedTabsDirective } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { OddsFormatterPipe } from '@frontend/sports/odds/feature';
import { DsButton } from '@frontend/ui/button';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsDivider } from '@frontend/ui/divider';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { EffectsModule } from '@ngrx/effects';
import { NgScrollbarModule } from 'ngx-scrollbar';

import { AccaBoostBaseModule } from '../../../lib/acca-boost-base/acca-boost-base.module';
import { ShowInEpcotDirective } from '../../../lib/common/epcot-config.directive';
import { EpcotNotificationComponent } from '../../../lib/common/epcotNotifications/notification.component';
import { TextTruncateComponent } from '../../../lib/common/text-truncate.component';
import BetslipBaseModule from '../../base/betslip-base.module';
import { RewardTokensActions } from './actions';
import { AcquisitionRewardNotificationComponent } from './components/digital/acquisition-reward-notification.component';
import { AcquisitionRewardOnboardingContainerComponent } from './components/digital/acquisition-reward-onboarding-container.component';
import { AccaBoostCardComponent } from './components/digital/cards/acca-boost/acca-boost-card.component';
import { BetAndGetCardComponent } from './components/digital/cards/betandget/betandget-card.component';
import { RewardTokenFooterDetailsComponent } from './components/digital/cards/footer-details/reward-token-footer-details.component';
import { FreeBetCardComponent } from './components/digital/cards/freebet/freebet-card.component';
import { OddsBoostCardComponent } from './components/digital/cards/odds-boost/odds-boost-card.component';
import { RewardTokenCardInfoLinkComponent } from './components/digital/cards/reward-token-card-info-link.component';
import { RewardTokenCategoriesComponent } from './components/digital/cards/reward-token-categories.component';
import { RewardTokenLimitationsComponent } from './components/digital/cards/reward-token-limitations.component';
import { RewardTokensCardComponent } from './components/digital/cards/reward-tokens-card.component';
import { RiskFreeCardComponent } from './components/digital/cards/risk-free/risk-free-card.component';
import { RewardTokensDrawerScrollDirective } from './components/digital/directives/reward-tokens-drawer-scroll.directive';
import { FreeBetStakeComponent } from './components/digital/freebet-stake.component';
import { HighlightToggleDirective } from './components/digital/highlight-toggle.directive';
import { RemindMeLaterNotificationComponent } from './components/digital/remind-me-later-notification.component';
import { RewardTokensBannerComponent } from './components/digital/reward-tokens-banner.component';
import { RewardTokensContainerComponent } from './components/digital/reward-tokens-container.component';
import { RewardTokensInfoComponent } from './components/digital/reward-tokens-info.component';
import { RewardTokensListComponent } from './components/digital/reward-tokens-list.component';
import { RewardTokensOnboardingContainerComponent } from './components/digital/reward-tokens-onboarding-container.component';
import { RewardTokensOnboardingComponent } from './components/digital/reward-tokens-onboarding.component';
import { RewardTokensSelectorContainerComponent } from './components/digital/reward-tokens-selector-container.component';
import { RewardTokensSelectorComponent } from './components/digital/reward-tokens-selector.component';
import { RewardsNotEligibleInfoComponent } from './components/digital/rewards-not-eligible-info/rewards-not-eligible-info.component';
import { AcquisitionRewardTokensEffects } from './effects/acquisition-reward-tokens.effects';
import { RewardTokenRecoveryEffects } from './effects/reward-token-recovery.effects';
import { RewardTokensDialogEffects } from './effects/reward-tokens-dialog.effects';
import { RewardTokensOnBoardingEffects } from './effects/reward-tokens-onboarding.effects';
import { RewardTokensSharedEffects } from './effects/reward-tokens-shared.effects';
import { RewardTokensTrackingEffects } from './effects/reward-tokens-tracking.effects';
import { RewardTokensEffects } from './effects/reward-tokens.effects';
import { RewardTokensModule } from './reward-tokens.module';
import { RewardTokensDialogService } from './services/reward-tokens-dialog.service';
import { RewardTokensValidationService } from './services/reward-tokens-validation.service';
import { RewardTokensValidatorsModule } from './services/validators/reward-tokens-validators.module';

@NgModule({
    declarations: [
        FreeBetStakeComponent,
        RewardTokensContainerComponent,
        RewardTokensCardComponent,
        OddsBoostCardComponent,
        FreeBetCardComponent,
        AccaBoostCardComponent,
        BetAndGetCardComponent,
        RewardTokenCategoriesComponent,
        RewardTokenLimitationsComponent,
        RewardTokensInfoComponent,
        RewardTokensOnboardingContainerComponent,
        RewardTokensBannerComponent,
        RiskFreeCardComponent,
        RewardTokensListComponent,
        RewardTokenCardInfoLinkComponent,
        RewardTokenFooterDetailsComponent,
        RewardTokensSelectorComponent,
        RewardTokensSelectorContainerComponent,
        HighlightToggleDirective,
        RewardTokensDrawerScrollDirective,
    ],
    imports: [
        CommonModule,
        NgScrollbarModule,
        RewardTokensValidatorsModule,
        AccaBoostBaseModule,
        EffectsModule.forFeature([
            RewardTokensDialogEffects,
            RewardTokensOnBoardingEffects,
            RewardTokensTrackingEffects,
            RewardTokensEffects,
            RewardTokenRecoveryEffects,
            RewardTokensSharedEffects,
            AcquisitionRewardTokensEffects,
        ]),
        BetslipBaseModule,
        RewardTokensModule,
        ShowInEpcotDirective,
        TextTruncateComponent,
        FontResizerComponent,
        EpcotNotificationComponent,
        RemindMeLaterNotificationComponent,
        TrustPipe,
        DsButton,
        IconCustomComponent,
        AcquisitionRewardOnboardingContainerComponent,
        RewardTokensOnboardingComponent,
        RewardsNotEligibleInfoComponent,
        AcquisitionRewardNotificationComponent,
        DsTabsModule,
        DsDivider,
        OddsFormatterPipe,
        DsButtonIcon,
        SportsThemedTabsDirective,
        NgOptimizedImage,
    ],
    exports: [
        FreeBetStakeComponent,
        RewardTokensContainerComponent,
        RewardTokensInfoComponent,
        RewardTokensOnboardingContainerComponent,
        RewardTokensBannerComponent,
        RewardTokensSelectorContainerComponent,
        RewardsNotEligibleInfoComponent,
    ],
    providers: [RewardTokensDialogService, RewardTokensValidationService],
})
export class DigitalRewardTokensModule {
    static readonly RewardTokensActions = RewardTokensActions;
}
